"use client";

import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDirection } from "@/utils/useDirection";
import { useTranslations } from "next-intl";
import { HiPause } from "react-icons/hi";

interface TFCarouselProps<T> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  autoPlayInterval?: number;
  className?: string;
  dotsClassName?: string;
  activeDotClassName?: string;
  inactiveDotClassName?: string;
}

function TFCarousel<T>({
  items,
  renderItem,
  autoPlayInterval = 5000,
  className = "",
  dotsClassName = "",
  activeDotClassName = "bg-indigo-600 w-8",
  inactiveDotClassName = "bg-slate-300 hover:bg-slate-400",
}: TFCarouselProps<T>) {
  const direction = useDirection();
  const isRTL = direction === "rtl";
  const t = useTranslations("web.components.TFCarousel");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (items.length === 0 || isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setSelectedIndex((prev) => {
        // In RTL, go backwards
        if (isRTL) {
          return prev === 0 ? items.length - 1 : prev - 1;
        }
        return (prev + 1) % items.length;
      });
    }, autoPlayInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [items.length, autoPlayInterval, isRTL, isPaused]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedIndex}
          initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: isRTL ? 20 : -20 }}
          transition={{ duration: 0.3 }}
          className="relative"
        >
          {renderItem(items[selectedIndex])}

          {/* Индикатор паузы в правом верхнем углу */}
          <AnimatePresence>
            {isPaused && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute top-3 end-3"
              >
                <HiPause className="w-8 h-8 text-black/30" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>

      {/* Навигация карусели */}
      <div
        className={`flex items-center justify-center gap-2 mt-6 ${dotsClassName}`}
      >
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => setSelectedIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === selectedIndex
                ? activeDotClassName
                : inactiveDotClassName
            }`}
            aria-label={t("goToItem", { index: index + 1 })}
          />
        ))}
      </div>
    </div>
  );
}

export default TFCarousel;
